import { Form } from "@inertiajs/react"
import { CheckCircle2, XCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import TiktokConnectCta from "@/components/social-accounts/tiktok-connect-cta"
import TiktokOAuthStartForm from "@/components/social-accounts/tiktok-oauth-start-form"
import TiktokScopeSummary from "@/components/social-accounts/tiktok-scope-summary"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import { useOnline } from "@/hooks/use-online"
import { settingsSocialAccountsTiktokRefreshPath } from "@/routes"

/**
 * BE-locked status union per BE TRD §"Settings/SocialAccounts/Show
 * props". `refresh_needed` collapses to `active` in the FE (UX Spec
 * v2 — "silent in UI"); only `revoked` surfaces a CTA.
 */
export type TiktokAccountStatus = "active" | "refresh_needed" | "revoked"

export interface TiktokAccount {
  id: number
  handle: string
  display_name: string | null
  avatar_url: string | null
  status: TiktokAccountStatus
  scopes: string[]
  linked_at: string
  last_refreshed_at: string | null
  token_expires_at: string | null
}

export interface TiktokAccountCardProps {
  /** Server-shipped account; `null` = never linked (defensive — should
   *  not happen post-OKS-22 but the empty state ships anyway). */
  tiktok_link: TiktokAccount | null
  /** Server-resolved POST URL — `tiktokReconnectPath()`. Only consumed
   *  on the `revoked` branch. */
  reconnect_url: string
  className?: string
}

/**
 * TiktokAccountCard — Settings → Social Accounts row.
 *
 * UX Spec §1.D + v2 amendments:
 *   - **`active`**: handle, display name, avatar, green dot + "Active"
 *     + last-verified timestamp + scope summary + a "Refresh TikTok
 *     profile" button (OKS-718 — Inertia `<Form>`, re-syncs avatar +
 *     display_name on demand). **NO Reconnect CTA** (CTPO ship-it lock).
 *   - **`refresh_needed`**: silent in UI — renders identically to
 *     `active`. Server-side refresh is silent; only `revoked` surfaces.
 *     Vitest **state-collapse canary** asserts active+refresh_needed
 *     both render NO Reconnect form (regression guard against future
 *     implementer adding a CTA on refresh_needed).
 *   - **`revoked`**: red dot + "Disconnected" copy + Reconnect CTA
 *     (TikTok-styled per CTPO tokens — same `<TiktokConnectCta>` used
 *     on OKS-22 Step 3, no fork).
 *   - **`tiktok_link=null`**: defensive empty state (shouldn't happen
 *     post-OKS-22 atomic create).
 *
 * **Reconnect uses plain HTML `<form>`** (NOT Inertia `<Form>`) per
 * `<TiktokOAuthStartForm>` — the BE controller 302s to tiktok.com,
 * which only follows from a top-level browser nav. CSRF token comes
 * from `<meta name="csrf-token">`.
 */
export function TiktokAccountCard({
  tiktok_link,
  reconnect_url,
  className = "",
}: TiktokAccountCardProps) {
  const { t } = useTranslation()
  const online = useOnline()

  if (!tiktok_link) {
    return (
      <Card
        data-testid="settings-social-accounts-tiktok-card"
        data-state="none"
        className={className}
      >
        <CardHeader>
          <CardTitle>{t("tiktok.account_card.title")}</CardTitle>
        </CardHeader>
        <CardContent
          data-testid="settings-social-accounts-tiktok-empty-state"
          className="text-muted-foreground text-sm"
        >
          {t("tiktok.account_card.empty")}
        </CardContent>
      </Card>
    )
  }

  // UX Spec v2 state-collapse: `refresh_needed` renders identically to
  // `active`. Only `revoked` shows a Reconnect CTA.
  const isRevoked = tiktok_link.status === "revoked"
  // `data-state` on the card root mirrors the BE-shipped value (NOT
  // the collapsed UI state) — preserves the wire shape for QA selectors
  // while the visible UI keeps refresh_needed silent.
  const dataState = tiktok_link.status

  // Status dot tone — UX Spec §1.D pairs colour + label, never colour
  // alone. The label below the dot makes the state announcement.
  const StatusIcon = isRevoked ? XCircle : CheckCircle2

  return (
    <Card
      data-testid="settings-social-accounts-tiktok-card"
      data-state={dataState}
      className={className}
    >
      <CardHeader>
        <CardTitle>{t("tiktok.account_card.title")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-start gap-4">
          <Avatar
            data-testid="settings-social-accounts-tiktok-avatar"
            className="size-10"
          >
            {tiktok_link.avatar_url && (
              <AvatarImage src={tiktok_link.avatar_url} alt="" />
            )}
            <AvatarFallback>
              {tiktok_link.handle.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span
                className="text-foreground font-mono text-sm font-medium"
                data-testid="settings-social-accounts-tiktok-handle"
              >
                @{tiktok_link.handle}
              </span>
              {tiktok_link.display_name && (
                <span
                  className="text-muted-foreground text-sm"
                  data-testid="settings-social-accounts-tiktok-display-name"
                >
                  {tiktok_link.display_name}
                </span>
              )}
            </div>
            <div
              className="flex items-center gap-1.5 text-xs"
              data-testid="settings-social-accounts-tiktok-status"
              data-state={dataState}
            >
              <StatusIcon
                className={
                  isRevoked
                    ? "size-3.5 text-red-500 dark:text-red-400"
                    : "size-3.5 text-emerald-500 dark:text-emerald-400"
                }
                aria-hidden="true"
              />
              <span
                className={isRevoked ? "text-red-600 dark:text-red-400" : ""}
              >
                {/* Two static `t()` calls (rather than `t(ternary)`) so
                    the inertia_i18n AST scanner can see both keys as
                    referenced — avoids spurious "unused" warnings on
                    `status.active` / `status.revoked`. */}
                {isRevoked
                  ? t("tiktok.account_card.status.revoked")
                  : t("tiktok.account_card.status.active")}
              </span>
            </div>
            {tiktok_link.last_refreshed_at && (
              <p
                className="text-muted-foreground text-xs"
                data-testid="settings-social-accounts-tiktok-last-refreshed-at"
              >
                {t("tiktok.account_card.last_refreshed_at", {
                  date: new Date(
                    tiktok_link.last_refreshed_at,
                  ).toLocaleString(),
                })}
              </p>
            )}
          </div>
        </div>

        <p
          className="text-muted-foreground text-xs"
          data-testid="settings-social-accounts-tiktok-linked-at"
        >
          {t("tiktok.account_card.linked_at", {
            date: new Date(tiktok_link.linked_at).toLocaleDateString(),
          })}
        </p>

        <TiktokScopeSummary
          scopeKeys={tiktok_link.scopes}
          data-testid="settings-social-accounts-tiktok-scopes"
        />

        {isRevoked && (
          <>
            <p
              className="text-sm"
              data-testid="settings-social-accounts-tiktok-helper-revoked"
            >
              {t("tiktok.account_card.helper.revoked")}
            </p>
            {/* Reconnect — plain HTML <form> (NOT Inertia <Form>). BE
                302s to tiktok.com which only follows from a top-level
                browser nav. See `<TiktokOAuthStartForm>` JSDoc. */}
            <TiktokOAuthStartForm
              action={reconnect_url}
              data-testid="settings-social-accounts-tiktok-reconnect-form"
            >
              <TiktokConnectCta
                disabled={!online}
                labelKey="tiktok.account_card.reconnect_cta"
                processingLabelKey="signup.tiktok.cta_processing"
                data-testid="settings-social-accounts-tiktok-reconnect"
              />
            </TiktokOAuthStartForm>
          </>
        )}
        {!isRevoked && (
          <>
            <p
              className="text-muted-foreground text-sm"
              data-testid="settings-social-accounts-tiktok-helper-active"
            >
              {t("tiktok.account_card.helper.active")}
            </p>
            {/* OKS-718 — manual "Refresh TikTok profile" (avatar +
                display_name), decoupled from token expiry. Inertia
                `<Form>` (NOT shadcn Form/RHF/zod) POSTing to the
                js-routes helper; the BE 303s back to Settings and the
                fresh avatar/name re-render via props, with a sonner
                toast driven by the layout `useFlash`. Rendered ONLY on
                the active branch (absent on revoked + null). Offline-
                disabled for parity with the Reconnect CTA. */}
            <Form
              action={settingsSocialAccountsTiktokRefreshPath()}
              method="post"
              options={{ preserveScroll: true }}
              data-testid="settings-social-accounts-tiktok-refresh-form"
            >
              {({ processing }) => (
                <Button
                  type="submit"
                  variant="outline"
                  disabled={processing || !online}
                  data-testid="settings-social-accounts-tiktok-refresh"
                  // OKS-826 (UX-Spec-OKS-718 §5) — ≥44px touch target. The
                  // primitive's `size.default` is `h-9` (36px); `min-h-11`
                  // (44px) is a *floor*, not a hard height, so it silently
                  // no-ops if OKS-874 raises the app-wide default rather than
                  // fighting it. Unconditional (no `sm:` reset) because a
                  // viewport width is not an input-modality signal — touch
                  // laptops report `sm+` with coarse pointers. Mirrors
                  // `brand-switcher.tsx` (OKS-854), not the hard-height
                  // `h-11 … sm:h-9` of the near-twin integration card.
                  className="min-h-11 w-full sm:w-auto"
                >
                  {processing && <Spinner className="size-4" />}
                  {processing
                    ? t("tiktok.account_card.refresh_processing")
                    : t("tiktok.account_card.refresh_cta")}
                </Button>
              )}
            </Form>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default TiktokAccountCard
